import { CalendarDate } from '@internationalized/date';
import * as aq from 'arquero';

import { filterByDateRange } from '../../../providers/TransactionsProvider';
import { TransactionGroupType } from '../utils';

import type { ISpendingChartDataPoint } from './SpendingChart';
import type { ColumnTable } from 'arquero';

interface IRollupDataItem {
  year: number;
  month?: number; // 1-indexed (CalendarDate format), optional for year grouping
  day?: number;
  income: number; // Sum of negative amounts (credits)
  spending: number; // Sum of positive amounts (debits)
  transactionCount: number;
}

function fillInMissingPeriods(
  rollupData: IRollupDataItem[],
  startDate: CalendarDate,
  endDate: CalendarDate,
  groupType: TransactionGroupType
): IRollupDataItem[] {
  const filledData: IRollupDataItem[] = [];

  if (groupType === TransactionGroupType.DAY) {
    // Fill in missing days up to endDate using CalendarDate
    let currentDate = startDate;

    while (currentDate.compare(endDate) <= 0) {
      const year = currentDate.year;
      const month = currentDate.month; // Keep 1-indexed
      const day = currentDate.day;

      const existing = rollupData.find((item) => item.year === year && item.month === month && item.day === day);

      filledData.push(
        existing || {
          year,
          month,
          day,
          income: 0,
          spending: 0,
          transactionCount: 0,
        }
      );

      currentDate = currentDate.add({ days: 1 });
    }
  } else if (groupType === TransactionGroupType.MONTH) {
    // Fill in missing months up to endDate using CalendarDate
    // Start at the first day of the start month
    let currentDate = startDate.set({ day: 1 });

    // Create a date for the first day of the end month for comparison
    const endMonthStart = endDate.set({ day: 1 });

    while (currentDate.compare(endMonthStart) <= 0) {
      const year = currentDate.year;
      const month = currentDate.month; // Keep 1-indexed

      const existing = rollupData.find((item) => item.year === year && item.month === month);

      filledData.push(
        existing || {
          year,
          month,
          income: 0,
          spending: 0,
          transactionCount: 0,
        }
      );

      currentDate = currentDate.add({ months: 1 });
    }
  } else if (groupType === TransactionGroupType.YEAR) {
    // Fill in missing years up to endDate
    // Start at Jan 1 of the start year
    let currentDate = startDate.set({ month: 1, day: 1 });

    // Create a date for Jan 1 of the end year for comparison
    const endYearStart = endDate.set({ month: 1, day: 1 });

    while (currentDate.compare(endYearStart) <= 0) {
      const year = currentDate.year;

      const existing = rollupData.find((item) => item.year === year);

      filledData.push(
        existing || {
          year,
          income: 0,
          spending: 0,
          transactionCount: 0,
        }
      );

      currentDate = currentDate.add({ years: 1 });
    }
  }

  return filledData;
}

interface IProcessDataOptions {
  transactions: ColumnTable;
  startDate: CalendarDate;
  endDate: CalendarDate;
  groupType: TransactionGroupType;
}

export function processSpendingChartData({
  transactions,
  startDate,
  endDate,
  groupType,
}: IProcessDataOptions): ISpendingChartDataPoint[] {
  // Filter by date range using CalendarDate comparison
  const filteredTable = filterByDateRange(transactions, startDate, endDate);

  // Separate income (negative amounts) and spending (positive amounts)
  // Plaid convention: positive = debit (spending), negative = credit (income)
  const incomeTable = filteredTable.filter(aq.escape((d: { amount: number }) => d.amount < 0));
  const spendingTable = filteredTable.filter(aq.escape((d: { amount: number }) => d.amount > 0));

  // Group income by day, month, or year
  let incomeRollup;
  if (groupType === TransactionGroupType.DAY) {
    incomeRollup = incomeTable
      .groupby({
        year: (d: { date: CalendarDate }) => d.date.year,
        month: (d: { date: CalendarDate }) => d.date.month, // Keep 1-indexed
        day: (d: { date: CalendarDate }) => d.date.day,
      })
      .rollup({
        income: aq.op.sum('amount'),
        transactionCount: aq.op.count(),
      })
      .orderby('year', 'month', 'day');
  } else if (groupType === TransactionGroupType.MONTH) {
    incomeRollup = incomeTable
      .groupby({
        year: (d: { date: CalendarDate }) => d.date.year,
        month: (d: { date: CalendarDate }) => d.date.month, // Keep 1-indexed
      })
      .rollup({
        income: aq.op.sum('amount'),
        transactionCount: aq.op.count(),
      })
      .orderby('year', 'month');
  } else {
    // YEAR grouping
    incomeRollup = incomeTable
      .groupby({
        year: (d: { date: CalendarDate }) => d.date.year,
      })
      .rollup({
        income: aq.op.sum('amount'),
        transactionCount: aq.op.count(),
      })
      .orderby('year');
  }

  // Group spending by day, month, or year
  let spendingRollup;
  if (groupType === TransactionGroupType.DAY) {
    spendingRollup = spendingTable
      .groupby({
        year: (d: { date: CalendarDate }) => d.date.year,
        month: (d: { date: CalendarDate }) => d.date.month, // Keep 1-indexed
        day: (d: { date: CalendarDate }) => d.date.day,
      })
      .rollup({
        spending: aq.op.sum('amount'),
        transactionCount: aq.op.count(),
      })
      .orderby('year', 'month', 'day');
  } else if (groupType === TransactionGroupType.MONTH) {
    spendingRollup = spendingTable
      .groupby({
        year: (d: { date: CalendarDate }) => d.date.year,
        month: (d: { date: CalendarDate }) => d.date.month, // Keep 1-indexed
      })
      .rollup({
        spending: aq.op.sum('amount'),
        transactionCount: aq.op.count(),
      })
      .orderby('year', 'month');
  } else {
    // YEAR grouping
    spendingRollup = spendingTable
      .groupby({
        year: (d: { date: CalendarDate }) => d.date.year,
      })
      .rollup({
        spending: aq.op.sum('amount'),
        transactionCount: aq.op.count(),
      })
      .orderby('year');
  }

  const incomeData = incomeRollup.objects() as Array<{
    year: number;
    month: number;
    day?: number;
    income: number;
    transactionCount: number;
  }>;
  const spendingData = spendingRollup.objects() as Array<{
    year: number;
    month: number;
    day?: number;
    spending: number;
    transactionCount: number;
  }>;

  // Helper to generate the map key based on grouping type
  const getKey = (item: { year: number; month?: number; day?: number }): string => {
    if (groupType === TransactionGroupType.DAY) {
      return `${item.year}-${item.month}-${item.day}`;
    } else if (groupType === TransactionGroupType.YEAR) {
      return `${item.year}`;
    }
    return `${item.year}-${item.month}`;
  };

  // Combine income and spending data
  const dataMap = new Map<string, IRollupDataItem>();

  // Add income data
  for (const item of incomeData) {
    const key = getKey(item);
    dataMap.set(key, {
      year: item.year,
      month: item.month,
      day: item.day,
      income: item.income,
      spending: 0,
      transactionCount: item.transactionCount,
    });
  }

  // Add spending data
  for (const item of spendingData) {
    const key = getKey(item);
    const existing = dataMap.get(key);
    if (existing) {
      existing.spending = item.spending;
      existing.transactionCount += item.transactionCount;
    } else {
      dataMap.set(key, {
        year: item.year,
        month: item.month,
        day: item.day,
        income: 0,
        spending: item.spending,
        transactionCount: item.transactionCount,
      });
    }
  }

  const rollupData = Array.from(dataMap.values());

  // Fill in missing periods with zeros up to endDate
  const filledData = fillInMissingPeriods(rollupData, startDate, endDate, groupType);

  // Format data for chart
  // Income is negative in data, convert to positive for display
  // Spending is positive in data, convert to negative for chart display
  return filledData.map((item) => {
    const income = Math.abs(item.income); // Convert negative to positive
    const spending = item.spending; // Keep positive as-is
    const spendingNegative = -spending; // Convert to negative for chart display
    const netAmount = income - spending; // Net = Income - Spending

    // Convert to CalendarDate for period string (YYYY-MM-DD)
    // For days, use exact date; for months, use first day of month; for years, use Jan 1
    let periodDate: CalendarDate;
    if (groupType === TransactionGroupType.DAY) {
      periodDate = new CalendarDate(item.year, item.month!, item.day!);
    } else if (groupType === TransactionGroupType.YEAR) {
      periodDate = new CalendarDate(item.year, 1, 1);
    } else {
      periodDate = new CalendarDate(item.year, item.month!, 1);
    }

    return {
      period: periodDate.toString(), // YYYY-MM-DD string
      income: Number(income.toFixed(2)),
      spending: Number(spending.toFixed(2)),
      spendingNegative: Number(spendingNegative.toFixed(2)),
      spacer: Number((-income).toFixed(2)), // Transparent spacer to reset stack to zero
      netAmount: Number(netAmount.toFixed(2)),
      // Point Y position: netAmount for positioning on chart
      pointY: item.transactionCount > 0 ? netAmount : undefined,
    };
  });
}
